/**
 * The engine choice as the user sees it, in one place.
 *
 * Two surfaces offer this choice — the onboarding bubble and the Settings
 * pocket — and both show the same one-line hint, so a Windows user comparing
 * the two screens is never told two different things.
 *
 * There is deliberately no third option for Windows 11's natural voices. They
 * ship encrypted and keyed to Narrator and Edge Read Aloud, and Microsoft
 * exposes them through neither SAPI nor OneCore — see the `windows_tts` module
 * docs for the detail.
 */
import { translate } from "@/i18n";
import { desktopVoiceMessages } from "@/i18n/messages/desktop-voice";
import { openWindowsSpeechSettings } from "./windows-speech-settings";

export type VoiceEngineChoice = "kokoro" | "system";

export type VoiceEngineOption = {
  id: VoiceEngineChoice;
  label: string;
  /** One line: what the engine trades, for onboarding and Settings alike. */
  hint: string;
};

export const VOICE_ENGINE_OPTIONS: VoiceEngineOption[] = [
  {
    id: "kokoro",
    get label() {
      return translate(desktopVoiceMessages, "engineDenker");
    },
    get hint() {
      return translate(desktopVoiceMessages, "engineDenkerHint");
    },
  },
  {
    id: "system",
    get label() {
      return translate(desktopVoiceMessages, "engineWindows");
    },
    get hint() {
      return translate(desktopVoiceMessages, "engineWindowsHint");
    },
  },
];

/** Windows voice ids are registry paths. The leaf is unique, readable, and —
 *  unlike the full path — usable in a `data-testid` without backslashes. */
export function voiceIdSlug(voiceId: string): string {
  const leaf = voiceId.split("\\").pop() ?? voiceId;
  return leaf || voiceId;
}

/** Open Windows Settings → Speech, reporting failure rather than swallowing it
 *  into an unhandled rejection. Returns the error if it could not be opened. */
export async function openWindowsSpeechSettingsSafely(): Promise<
  string | null
> {
  try {
    await openWindowsSpeechSettings();
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}
